import { loadFormsPage } from "@/lib/admin/loadFormsList";
import {
  formsListIsClosedParam,
  formsListSourceParam,
  formsListViewFromWorkspace,
  toFormsListSortParam,
} from "@/lib/admin/formsListQuery";
import type {
  FormSourceFilter,
  FormStatusFilter,
} from "@/lib/admin/formListFilters";
import type {
  FormsListSortBy,
  FormsListSortDir,
} from "@/lib/admin/formsListPreferences";
import { formatApiQueryError } from "@/lib/http/formatQueryError";
import type { FormListItemDto, PaginatedMeta } from "@/types/formList";
import { useAsyncResource } from "./useAsyncResource";

interface UseFormsListQueryOptions {
  organisationId: number | null;
  unassignedOnly?: boolean;
  grantedOnly?: boolean;
  serviceOnly?: boolean;
  page: number;
  count: number;
  search: string;
  status: FormStatusFilter;
  source: FormSourceFilter;
  sortBy: FormsListSortBy;
  sortDir: FormsListSortDir;
}

interface UseFormsListQueryResult {
  forms: FormListItemDto[];
  meta: PaginatedMeta | null;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

export function useFormsListQuery(
  options: UseFormsListQueryOptions,
): UseFormsListQueryResult {
  const {
    organisationId,
    unassignedOnly = false,
    grantedOnly = false,
    serviceOnly = false,
    page,
    count,
    search,
    status,
    source,
    sortBy,
    sortDir,
  } = options;

  const view = formsListViewFromWorkspace({
    unassigned: unassignedOnly,
    granted: grantedOnly,
    service: serviceOnly,
  });

  const { data, isLoading, error, reload } = useAsyncResource({
    enabled: organisationId != null || view != null,
    deps: [
      organisationId,
      view,
      page,
      count,
      search,
      status,
      source,
      sortBy,
      sortDir,
    ],
    fetch: () =>
      loadFormsPage({
        page,
        count,
        search,
        isClosed: formsListIsClosedParam(status),
        source: formsListSourceParam(source),
        sort: toFormsListSortParam(sortBy, sortDir),
        organisationId: organisationId ?? undefined,
        view,
      }),
    formatError: (err) =>
      formatApiQueryError(err, "Не удалось загрузить список форм", {
        statusPrefix: "Не удалось загрузить список форм",
      }),
  });

  return {
    forms: data?.data ?? [],
    meta: data?.meta ?? null,
    isLoading,
    error,
    reload,
  };
}

export { FORMS_LIST_PER_PAGE_OPTIONS } from "./formsListPerPage";
